/**
 * AI Coach card — Home screen.
 *
 * Five states (per spec):
 *   A. no_session    — "Start a workout to activate your Personal AI Trainer."
 *   B. loading       — "Your AI Trainer is analysing your last session…" (subtle pulse)
 *   C. message_ready — Latest AI Coach message preview + "View full message →"
 *   D. trainer_response_available — Phase 3 (priority over C). Stub today.
 *   E. rest_day      — "Your AI Trainer recommends rest today." + See why.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ChevronRight, Brain } from "lucide-react";
import { apiClient } from "../../services/api";

export default function AICoachCard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    const load = () => {
      apiClient
        .get("/ai-trainer/coach/latest")
        .then((r) => alive && setData(r.data))
        .catch(() => alive && setData({ state: "no_session" }));
    };
    load();
    // Refresh every 30s while card is mounted (background coach finishes async)
    const id = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  if (!data) return null;
  const state = data.state;

  return (
    <button
      type="button"
      onClick={() =>
        state === "no_session" ? navigate("/workouts") : navigate("/ai-trainer/coach")
      }
      data-testid="ai-coach-card"
      data-state={state}
      className="w-full text-left bg-white rounded-[16px] p-4 border border-line2"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Brain size={14} style={{ color: "#00B0D5" }} />
        <span
          className="text-[10px] uppercase tracking-[0.14em] font-bold"
          style={{ color: "#00B0D5" }}
        >
          Your AI Trainer
        </span>
      </div>

      {state === "no_session" && (
        <div data-testid="coach-state-no-session">
          <div className="font-barlow-cond font-extrabold text-[18px] leading-tight">
            Start a workout to activate your Personal AI Trainer.
          </div>
          <div className="mt-2 inline-flex items-center gap-1 text-[12px] font-bold" style={{ color: "#00B0D5" }}>
            Begin Workout <ChevronRight size={13} />
          </div>
        </div>
      )}

      {state === "loading" && (
        <div data-testid="coach-state-loading" className="flex items-center gap-3">
          <div
            className="w-2 h-2 rounded-full"
            style={{
              background: "#00D4FF",
              animation: "pat-coach-pulse 1.2s ease-in-out infinite",
            }}
          />
          <div className="text-[14px]" style={{ color: "#6B6760" }}>
            Your AI Trainer is analysing your last session…
          </div>
          <style>{`@keyframes pat-coach-pulse {
            0%, 100% { opacity: 0.4; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.4); }
          }`}</style>
        </div>
      )}

      {state === "rest_day" && (
        <div data-testid="coach-state-rest">
          <div className="font-barlow-cond font-extrabold text-[18px] leading-tight">
            Your AI Trainer recommends rest today.
          </div>
          <div className="mt-1.5 text-[13px]" style={{ color: "#6B6760" }}>
            Your form quality and recovery suggest your body needs a recovery day.
          </div>
          <div className="mt-2 inline-flex items-center gap-1 text-[12px] font-bold" style={{ color: "#00B0D5" }}>
            See why <ChevronRight size={13} />
          </div>
        </div>
      )}

      {state === "message_ready" && data.message && (
        <div data-testid="coach-state-message">
          <p
            className="text-[14px] leading-snug"
            style={{
              color: "#1A1A1A",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {data.message.text}
          </p>
          <div className="mt-2 inline-flex items-center gap-1 text-[12px] font-bold" style={{ color: "#00B0D5" }}>
            View full message <ChevronRight size={13} />
          </div>
        </div>
      )}
    </button>
  );
}
